"use client";

import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Ticket KN6: EIN Beobachter für Anmeldewechsel je Seite.
 *
 * Bisher setzte jede Hook-Instanz einen eigenen Beobachter auf. Bei sechs
 * Instanzen führte ein Kontowechsel deshalb zu sechs Leerungen und sechs
 * Nachladungen. Der Zähler unten sorgt dafür, dass genau ein Beobachter aktiv
 * ist, solange mindestens eine Instanz lebt.
 *
 * Token-Erneuerungen (`TOKEN_REFRESHED`, `INITIAL_SESSION`) lösen bewusst
 * nichts aus, sonst lädt die Seite bei jeder Erneuerung neu.
 *
 * Fehlerbehebung Cockpit-Menü: Die Handler bekommen jetzt Ereignis UND
 * Nutzerkennung. `SIGNED_IN` kann auch für dasselbe Konto auftreten (z. B.
 * beim Zurückkehren in einen lange offenen Reiter). Ohne diese Angabe wurde
 * dann der Sitzungsspeicher geleert, das Cockpit zeigte kurz einen
 * Ladebildschirm und Klicks in der Seitenleiste gingen verloren.
 */

export type AuthChangeInfo = {
  event: "SIGNED_IN" | "SIGNED_OUT" | "USER_UPDATED";
  /** Kennung des angemeldeten Kontos; bei Abmeldung `null`. */
  userId: string | null;
};

type Handler = (info: AuthChangeInfo) => void;

let refCount = 0;
let subscription: { unsubscribe: () => void } | null = null;
const handlers = new Set<Handler>();

function ensureSubscription(): void {
  if (subscription) return;
  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
    const info: AuthChangeInfo = { event, userId: session?.user?.id ?? null };
    for (const handler of Array.from(handlers)) handler(info);
  });
  subscription = data.subscription;
}

/** Ruft `onAuthChange` bei An-/Abmeldung und Kontowechsel auf. */
export function useAuthChange(onAuthChange: Handler): void {
  const ref = useRef(onAuthChange);
  ref.current = onAuthChange;

  useEffect(() => {
    const handler: Handler = (info) => ref.current(info);
    handlers.add(handler);
    refCount += 1;
    ensureSubscription();

    return () => {
      handlers.delete(handler);
      refCount -= 1;
      if (refCount <= 0) {
        refCount = 0;
        subscription?.unsubscribe();
        subscription = null;
      }
    };
  }, []);
}
